import { authUrl, hasManualTradingOrigin } from '@/config/api';
import {
  clearTradingSessionBootstrap,
  isTradingSessionBootstrapped,
  markTradingSessionBootstrapped,
} from '@/auth/tradingSessionBootstrap';
import { clearAccessToken, getAccessToken, setAccessToken } from '@/auth/tokenStore';
import {
  loadManualTradingSession,
  ManualTradingMisconfiguredError,
} from '@/services/manualTradingApi';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type AuthUser = {
  user_id: string;
  username: string;
  email: string;
  roles: string[];
};

type AuthContextValue = {
  user: AuthUser | null;
  /** Auth identity resolved (logged in or confirmed guest). */
  isReady: boolean;
  /** `/loadme` finished or skipped; safe to call trading-engine APIs. */
  isTradingSessionReady: boolean;
  login: (username: string, password: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  register: (username: string, password: string, email: string, verificationCode: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function fetchMe(token: string): Promise<AuthUser> {
  const res = await fetch(authUrl('me'), {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`me ${res.status}`);
  }
  return (await res.json()) as AuthUser;
}

/** 须在 `setAccessToken` 之后调用：`loadManualTradingSession` 从 tokenStore 取 Bearer */
async function bootstrapManualTradingForUser(userId: string): Promise<void> {
  if (!hasManualTradingOrigin()) {
    return;
  }
  if (isTradingSessionBootstrapped(userId)) {
    return;
  }
  try {
    await loadManualTradingSession();
    markTradingSessionBootstrapped(userId);
  } catch (e) {
    if (e instanceof ManualTradingMisconfiguredError) {
      console.error(e.message);
      return;
    }
    throw e;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isTradingSessionReady, setIsTradingSessionReady] = useState(!hasManualTradingOrigin());

  const finishTradingBootstrap = useCallback(async (nextUser: AuthUser | null): Promise<void> => {
    if (!nextUser || !hasManualTradingOrigin()) {
      setIsTradingSessionReady(true);
      return;
    }
    setIsTradingSessionReady(false);
    try {
      await bootstrapManualTradingForUser(nextUser.user_id);
    } finally {
      setIsTradingSessionReady(true);
    }
  }, []);

  const refreshMe = async (): Promise<void> => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    const token = getAccessToken();
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
    const refreshRes = await fetch(authUrl('refresh'), {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({}),
    });
    if (!refreshRes.ok) {
      clearAccessToken();
      setUser(null);
      setIsTradingSessionReady(!hasManualTradingOrigin());
      return;
    }
    const tokenPayload = (await refreshRes.json()) as { access_token?: string };
    if (!tokenPayload.access_token) {
      clearAccessToken();
      setUser(null);
      setIsTradingSessionReady(!hasManualTradingOrigin());
      return;
    }
    setAccessToken(tokenPayload.access_token);
    const me = await fetchMe(tokenPayload.access_token);
    setUser(me);
    try {
      await finishTradingBootstrap(me);
    } catch {
      clearAccessToken();
      setUser(null);
      setIsTradingSessionReady(!hasManualTradingOrigin());
    }
  };

  const login = async (username: string, password: string): Promise<void> => {
    const res = await fetch(authUrl('login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `login ${res.status}`);
    }
    const payload = (await res.json()) as {
      user: AuthUser;
      tokens: { access_token: string };
    };
    const access = payload.tokens.access_token;
    if (!access) {
      throw new Error('login response malformed');
    }
    setAccessToken(access);
    setUser(payload.user);
    await finishTradingBootstrap(payload.user);
  };

  const loginWithGoogle = async (credential: string): Promise<void> => {
    const res = await fetch(authUrl('google'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ credential }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `google login ${res.status}`);
    }
    const payload = (await res.json()) as {
      user: AuthUser;
      tokens: { access_token: string };
    };
    const access = payload.tokens.access_token;
    if (!access) {
      throw new Error('google login response malformed');
    }
    setAccessToken(access);
    setUser(payload.user);
    await finishTradingBootstrap(payload.user);
  };

  const register = async (
    username: string,
    password: string,
    email: string,
    verificationCode: string
  ): Promise<void> => {
    const res = await fetch(authUrl('register'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password, email, verification_code: verificationCode }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `register ${res.status}`);
    }
    const payload = (await res.json()) as {
      user: AuthUser;
      tokens: { access_token: string };
    };
    const access = payload.tokens.access_token;
    if (!access) {
      throw new Error('register response malformed');
    }
    setAccessToken(access);
    setUser(payload.user);
    await finishTradingBootstrap(payload.user);
  };

  const logout = async (): Promise<void> => {
    try {
      await fetch(authUrl('logout'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      }).catch(() => {});
    } catch {
      /* noop */
    }
    clearAccessToken();
    clearTradingSessionBootstrap();
    setUser(null);
    setIsTradingSessionReady(!hasManualTradingOrigin());
  };

  useEffect(() => {
    const init = async () => {
      let resolvedUser: AuthUser | null = null;
      try {
        const token = getAccessToken();
        if (token) {
          resolvedUser = await fetchMe(token);
          setUser(resolvedUser);
        } else {
          await refreshMe();
          setIsReady(true);
          return;
        }
      } catch {
        clearAccessToken();
        setUser(null);
      } finally {
        setIsReady(true);
      }
      try {
        await finishTradingBootstrap(resolvedUser);
      } catch {
        clearAccessToken();
        setUser(null);
        setIsTradingSessionReady(!hasManualTradingOrigin());
      }
    };
    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot bootstrap
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isReady, isTradingSessionReady, login, loginWithGoogle, register, logout, refreshMe }),
    [user, isReady, isTradingSessionReady]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
